import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { isInLast24Hours, localeAwareFormat, localeAwareFormatDistance } from 'app/lib/date';
import { CustomChannelType, CustomMessageType, isChannelSilenced, MessageKind } from 'app/types/chat';
import type { Message, Channel as ChannelType } from 'app/types/chat';
import type { ReactNode, RefObject } from 'react';
import { Placeholder, PlaceholderLine, PlaceholderMedia, ShineOverlay } from 'app/overrides/rn-placeholder';
import { ListingMatchStatus, ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { type View, type ViewProps } from 'react-native';
import { useAtomValue } from 'jotai';
import { optimisticChannelsReadStatusAtom } from 'app/atoms';
import { useUser } from 'app/network/user';
import { useTranslation } from 'react-i18next';
import { useGetInterlocutor } from 'app/network/chat';
import { Signal } from 'app/design/icons/signal';
import { getSharedListingIdFromAppUrl, getSharedUserProfileIdFromAppUrl } from 'app/utils/helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconBellOff } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface ChannelProps {
  channel: ChannelType;
  prefix: ReactNode;
  innerRef?: RefObject<View | null>;
  statusBadgeContent?: ReactNode;
}

export const ConciergeSignal = function ConciergeSignal() {
  return (
    <Column
      alignItems="center"
      justifyContent="center"
      style={{
        zIndex: 1,
        backgroundColor: theme.colors['surface.tertiary'],
        borderRadius: theme.borderRadii['radius.full'],
        width: 22,
        height: 22,
        boxShadow: `0px 0px 4px ${theme.colors['surface.tertiary']}`,
      }}
    >
      <Signal height={16} width={16} color={theme.colors['text.primary']} />
    </Column>
  );
};

const ChannelRow = function Channel({ channel, prefix, innerRef, statusBadgeContent }: ChannelProps) {
  const { t } = useTranslation('all-channels');
  const { t: t2 } = useTranslation('one-channel');
  const { data: user } = useUser();
  const getInterlocutor = useGetInterlocutor();
  const interlocutor = getInterlocutor(channel);

  const getLastMessageText = (lastMessage: Message) => {
    if (lastMessage.type === MessageKind.File) {
      return t('file');
    }
    if (getSharedListingIdFromAppUrl(lastMessage.og_tag?.['og:url'])) {
      return t('sharedListing', { sender: lastMessage.user?.firstname });
    }
    if (getSharedUserProfileIdFromAppUrl(lastMessage.og_tag?.['og:url'])) {
      return t('sharedProfile', { sender: lastMessage.user?.firstname });
    }

    if (lastMessage.custom_type === CustomMessageType.Loan) {
      const isService = lastMessage.data.listingSubtype === ListingSubtype.service;
      const transactionMode =
        channel.linkedOffer?.transactionMode ??
        channel.listing?.transactionMode ??
        lastMessage.loan.listing.transactionMode;
      const isSell = transactionMode === TransactionMode.sell;
      const isDonation = isDonationListing({
        isDonation: lastMessage.loan.listing.isDonation,
        subtype: lastMessage.data.listingSubtype ?? lastMessage.loan.listing.subtype,
        transactionMode,
      });
      const tPrefix = isSell ? 'sell' : isService ? 'service' : isDonation ? 'donation' : 'loan';
      return t(`${tPrefix}.${lastMessage.data.loanStatus}`);
    }

    if (
      lastMessage.custom_type === CustomMessageType.ConciergePrompt &&
      channel.custom_type === CustomChannelType.Listing
    ) {
      const isBorrower = channel.listingUserId === user?.id;
      if (isBorrower) {
        return t2('conciergeMessage.matchAccepted.lastMessageText', { interlocutorName: interlocutor?.firstname });
      } else {
        if (channel.needMatch?.status === ListingMatchStatus.ACCEPTED) {
          return t2('conciergeMessage.canHelp.lastMessageText', { interlocutorName: interlocutor?.firstname });
        } else if (channel.needMatch?.status === ListingMatchStatus.REJECTED) {
          return t2('conciergeMessage.cantHelp.lastMessageText', { interlocutorName: interlocutor?.firstname });
        } else if (channel.needMatch?.status === ListingMatchStatus.RESOLVED) {
          return t2('conciergeMessage.resolved.lastMessageText', { lenderName: interlocutor?.firstname });
        } else {
          return t2('conciergeMessage.canYouHelp.lastMessageText', { interlocutorName: interlocutor?.firstname });
        }
      }
    }

    if (typeof lastMessage.message === 'string') {
      return lastMessage.message.trim();
    }

    const localizedMessage = typeof lastMessage.message === 'object' ? lastMessage.message : undefined;

    if (localizedMessage?.i18nKey) {
      return t(localizedMessage.i18nKey, {
        ns: localizedMessage.ns,
        ...localizedMessage.values,
      })
        .replace('<strong>', '')
        .replace('</strong>', '');
    }
  };

  const channelsReadStatus = useAtomValue(optimisticChannelsReadStatusAtom);

  const markChannelAsUnread = channel.unread_message_count > 0 && !channelsReadStatus[channel.channel_url];

  const channelSilenced = isChannelSilenced(channel);

  const isConciergeChannel = channel.custom_type === CustomChannelType.Listing && !!channel.needMatch;

  return (
    <Row flex={1} gap="spacing.general.x3" innerRef={innerRef} alignItems="center">
      {prefix}

      <Column flex={1} justifyContent="space-around" gap="spacing.general.x0,5">
        <Row alignItems="center" justifyContent="space-between">
          <Row gap="spacing.general.x1" alignItems="center" flex={1}>
            {isConciergeChannel ? <ConciergeSignal /> : null}
            <Row
              alignItems="center"
              flex={1}
              justifyContent="space-between"
              gap="spacing.general.x1"
              style={{ minWidth: 0 }}
            >
              <Text fontWeight="700" numberOfLines={1} variant="bodyBase" flex={1} style={{ minWidth: 0 }}>
                {channel.name}
              </Text>
              <Row alignItems="center" gap="spacing.general.x1">
                {channelSilenced ? (
                  <IconBellOff
                    strokeWidth={ICON_STROKE_WIDTH}
                    size={16}
                    color={theme.colors['text.secondary']}
                    style={{ marginStart: theme.spacing['spacing.general.x1'] }}
                  />
                ) : null}
                {markChannelAsUnread ? (
                  <Column
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      backgroundColor: theme.colors['icon.error'],
                      borderRadius: theme.borderRadii['radius.full'],
                      minWidth: 8,
                      height: 8,
                    }}
                  />
                ) : null}
              </Row>
            </Row>
          </Row>
        </Row>

        {!!channel.last_message && (
          <Row gap="spacing.general.x2">
            <Text
              fontWeight={markChannelAsUnread ? '600' : undefined}
              numberOfLines={1}
              style={{ flex: 1 }}
              variant="bodyBase"
            >
              {getLastMessageText(channel.last_message)}
            </Text>

            <Text color="text.placeholder" variant="bodyBase">
              {localeAwareFormat(
                channel.last_message.created_at,
                isInLast24Hours(new Date(channel.last_message.created_at)) ? 'HH:mm' : 'yyyy-MM-dd',
              )}
            </Text>
          </Row>
        )}

        {statusBadgeContent}

        {!!channel.last_message &&
        channel.warn_no_response &&
        channel.last_message.created_at &&
        channel.custom_type !== CustomChannelType.Group ? (
          <Text color="surface.error.default" variant="bodyBase">
            {t('noResponse', { ago: localeAwareFormatDistance(new Date(), new Date(channel.last_message.created_at)) })}
          </Text>
        ) : null}
      </Column>
    </Row>
  );
};

const Skeleton = function Skeleton() {
  const leftElement = (props: ViewProps) => (
    <PlaceholderMedia
      style={[
        {
          height: 60,
          width: 60,
          borderRadius: theme.borderRadii['radius.lg'],
          marginEnd: theme.spacing['spacing.general.x4'],
        },
        props.style,
      ]}
    />
  );

  return (
    <Placeholder Animation={ShineOverlay} Left={leftElement}>
      <PlaceholderLine style={{ height: 10, width: 200, borderRadius: theme.borderRadii['radius.lg'] }} />
      <PlaceholderLine style={{ height: 7.5, width: 125, borderRadius: theme.borderRadii['radius.lg'] }} />
    </Placeholder>
  );
};

export { ChannelRow, Skeleton };
